<template>
  <div>
    <Title>合同额</Title>
    <div class="total">
      <span class="money-color">
        <Money :money="detail.extra.contractData.contractAmount" />
      </span>
      <span class="unit">万元</span>
    </div>

    <Title>合同信息</Title>
    <a-descriptions :column="2" bordered>
      <a-descriptions-item label="考核年份">
        <a-select 
          v-if="isContractSubpackageFirstReview" 
          :disabled="disabled"
          :value="assessmentYear" 
          @change="handleAssessmentYearChange"
          style="width: 100%"
        >
          <a-select-option
            v-for="item in assessmentYearList"
            :key="item.value"
            :value="item.value"
          >{{ item.name }}</a-select-option>
        </a-select>
        <span v-else>
          <DataDictFinder v-if="detail.extra.contractData.assessmentYear" dictType="pallocateCalcFormula" :dictValue="detail.extra.contractData.assessmentYear" />
          <span v-else>--</span>
        </span>
      </a-descriptions-item>
      <a-descriptions-item label="承接方式">
        <a-select 
          v-if="isContractSubpackageFirstReview" 
          :disabled="disabled"
          :value="undertakingMode" 
          @change="handleUndertakingModeChange"
          style="width: 100%"
        >
          <a-select-option
            v-for="item in undertakingModeList"
            :key="item.value"
            :value="item.value"
          >{{ item.name }}</a-select-option>
        </a-select>
        <span v-else>
          <DataDictFinder v-if="detail.extra.contractData.undertakingMode" dictType="contract_undertaking_method" :dictValue="detail.extra.contractData.undertakingMode" />
          <span v-else>--</span>
        </span>
      </a-descriptions-item>
      <a-descriptions-item label="经营组">
        {{ detail.extra.contractData.businessGroup || '--' }}
      </a-descriptions-item>
      <a-descriptions-item label="经营对接人">
        <div v-if="isContractSubpackageFirstReview">
          <BusinessContactSelector
            :disabled="disabled"
            @change="handleBusinessContactUserChange"
            :value="{
              businessGroupId: businessGroupId,
              businessGroup: businessGroup,
              businessContactUserId: businessContactUserId,
              businessContactUserName: businessContactUserName
            }"
          />
          <div v-if="businessContactValidateStatus === 'error'" style="color: #f5222d; font-size: 12px; margin-top: 4px;">
            {{ businessContactHelp }}
          </div>
        </div>
        <span v-else>
          {{ detail.extra.contractData.businessContactUserName || '--' }}
        </span>
      </a-descriptions-item>
    </a-descriptions>

    <Title>分包明细</Title>
    <a-table :data-source="detail.extra.contractData.subPackages" :pagination="false" bordered>
      <a-table-column title="分包名称" data-index="name" />
      <a-table-column title="分包类型">
        <template slot-scope="text">
          <div class="left">
            <DataDictFinder v-if="text.isneeded === 1" dictType="subType" :dictValue="text.type" />
            <DataDictFinder v-if="text.isneeded === 0" dictType="notNeedSubType" :dictValue="text.type" />
            <DataDictFinder v-if="text.isneeded === 2" dictType="generalSubType" :dictValue="text.type" />
            <template v-if="text.major">
              <span> - </span>
              <DataDictFinder dictType="majorTypes" :dictValue="text.major" />
            </template>
          </div>
        </template>
      </a-table-column>
      <a-table-column title="分包界定">
        <template slot-scope="text">
          <div>
            <span v-if="text.isneeded === 0" style="color: #f00">
              设计类-非必要分包
            </span>
            <span v-if="text.isneeded === 1" style="color: #1890ff">
              设计类-必要分包
            </span>
            <span v-if="text.isneeded === 2" style="color: green">
              总承包类分包
            </span>
          </div>
        </template>
      </a-table-column>

      <a-table-column title="公司" data-index="companyName" width="240px" ellipsis="true"></a-table-column>

      <a-table-column title="分包状态" data-index="status" align="center">
        <template slot-scope="status">
          <div>
            <DataDictFinder dictType="subpackageStatus" :dictValue="status" iconType="badge" badgeType="color" />
          </div>
        </template>
      </a-table-column>

      <a-table-column title="分包金额(万元)" data-index="planAmount" align="right" width="100px">
        <template slot-scope="planAmount">
          <Money :money="planAmount" />
        </template>
      </a-table-column>

      <a-table-column key="files" title="附件">
        <template slot-scope="text">
          <span v-if="text.attachments">
            <a v-for="(item, index) in text.attachments.split(',')" :key="index" :href="item" download target="_blank">
              {{ $getFileName(item) }}
            </a>
          </span>
        </template>
      </a-table-column>
    </a-table>
  </div>
</template>

<script>
import Title from "../Title";
import BusinessContactSelector from "@/components/business-contact-selector";
import { mapState, mapGetters, mapMutations } from "vuex";
export default {
  components: {
    Title,
    BusinessContactSelector,
  },
  data() {
    return {
      businessContactValidateStatus: '', // 经营对接人验证状态
      businessContactHelp: '', // 经营对接人验证提示信息
    };
  },
  computed: {
    ...mapState("approval", ["detail"]),
    ...mapGetters("approval", [
      "isContractSubpackageFirstReview", // 是否是合同分包初审节点
      "assessmentYear", // 考核年份
      "undertakingMode", // 承接方式
      "businessGroupId", // 经营组ID
      "businessGroup", // 经营组名称
      "businessContactUserId", // 经营对接人ID
      "businessContactUserName" // 经营对接人名称
    ]),
    ...mapGetters("setting", ["findDataDict"]),

    // 考核年份列表
    assessmentYearList() {
      return this.findDataDict("pallocateCalcFormula");
    },

    // 承接方式列表
    undertakingModeList() {
      return this.findDataDict("contract_undertaking_method");
    },

    // 经营组列表
    businessGroupList() {
      return []; // 暂时返回空数组，后续根据实际数据源填充
    },

    // 是否禁用编辑
    disabled() {
      return this.detail.status !== "ASSIGNED" && this.detail.status !== "RESUBMITED";
    },
  },
  methods: {
    ...mapMutations("approval", [
      "setAssessmentYear", // 设置考核年份
      "setUndertakingMode", // 设置承接方式
      "setBusinessGroupId", // 设置经营组ID
      "setBusinessGroup", // 设置经营组名称
      "setBusinessContactUserId", // 设置经营对接人ID
      "setBusinessContactUserName" // 设置经营对接人名称
    ]),

    handleAssessmentYearChange(value) {
      this.setAssessmentYear(value);
    },

    handleUndertakingModeChange(value) {
      this.setUndertakingMode(value);
      // 触发经营对接人验证
      this.$nextTick(() => {
        this.validateBusinessContactUser();
      });
    },

    // 验证经营对接人
    validateBusinessContactUser() {
      // 承接方式为增量时必填（值为 contract_undertaking_method_increment）
      if (this.undertakingMode === 'contract_undertaking_method_increment') {
        if (!this.businessContactUserId) {
          this.businessContactValidateStatus = 'error';
          this.businessContactHelp = '承接方式为增量时，经营对接人必填！';
          return false;
        }
      }

      // 承接方式不是增量，或者经营对接人已填写，清除错误提示
      this.businessContactValidateStatus = '';
      this.businessContactHelp = '';
      return true;
    },

    // 处理经营对接人选择
    handleBusinessContactUserChange(data) {
      if (data.businessContactUserId) {
        this.setBusinessContactUserId(data.businessContactUserId);
        this.setBusinessContactUserName(data.businessContactUserName);
        this.setBusinessGroupId(data.businessGroupId);
        this.setBusinessGroup(data.businessGroup);
        // 清除验证错误
        this.businessContactValidateStatus = '';
        this.businessContactHelp = '';
      } else {
        this.setBusinessContactUserId(null);
        this.setBusinessContactUserName(null);
        this.setBusinessGroupId(null);
        this.setBusinessGroup(null);
        // 触发验证（可能需要显示错误提示）
        this.$nextTick(() => {
          this.validateBusinessContactUser();
        });
      }
    },
  },

  mounted() {
    // 初始化时触发验证（用于显示初始错误状态）
    this.$nextTick(() => {
      this.validateBusinessContactUser();
    });
  },
};
</script>

<style lang="less" scoped>
.total {
  margin-left: 18px;
  font-size: 24px;

  .unit {
    font-size: 12px;
    padding-left: 8px;
  }
}
</style>